// ── A LABELLED YES / NO SEGMENTED TOGGLE ─────────────────────────────────────────────────────────
// Ryan 2026-08-12: *"replace it with a toggle with the same UX/UI as the Lettering case toggle,
// whereby one side can say 'Yes' … and one side can say 'No'. Perhaps the left button/toggle is no,
// right is yes."*
//
// It is literally the Lettering-case control — `.wd-unit-toggle--lg` with two `.wd-unit-btn`s, the
// same markup the LB/KG switch uses — with a caption above it, because unlike ALL CAPS / As typed
// these answer a QUESTION and the question has to be on screen.
//
// ⛔ ONE FACTORY, NOT A PATTERN TO COPY. Three of these exist (two on the Add Logo tab, one in
// Customize Design → Logo) and a fourth is likely. Hand-rolling the markup per site is how the
// layout picker's carousel and its View-all grid ended up ordering themselves differently
// (project CLAUDE.md §0b) — and here it would also mean three places deciding independently which
// side "Yes" sits on.
//
// ⚠ NO / YES ORDER IS DELIBERATE and matches the request. It also matches the two existing
// segmented toggles in the app, which both put the *conservative* / default-ish answer on the left.
import { h, replaceChildren } from './dom.js';

/**
 * @param {object} opts
 * @param {string} opts.label      the question, shown above the control
 * @param {boolean} [opts.caption=true] draw `label` above the control. FALSE where the surrounding
 *   surface already heads the control — Customize Design gives it a `.wd-subhead` like every other
 *   control in that pane, and two labels would be two names for one switch. `label` is still used
 *   for the accessible name, so turning the caption off never costs the group its name.
 * @param {string} [opts.ariaLabel] group label for assistive tech; defaults to `label`
 * @param {boolean} [opts.value]   initial answer
 * @param {(v: boolean) => void} opts.onChange fired ONLY on a real change, with the new answer
 * @returns {{el: HTMLElement, set(v: boolean): void, get(): boolean, setBusy(b: boolean): void}}
 *   `el` carries `hidden` like the label it replaces, so every existing show/hide site is unchanged.
 */
export function yesNoToggle({ label, caption = true, ariaLabel, value = false, onChange }) {
  let val = !!value;
  let busy = false;
  const wrap = h('div', { class: 'wd-unit-toggle wd-unit-toggle--lg', role: 'group', 'aria-label': ariaLabel || label });
  const el = h('div', { class: 'wd-yn' + (caption ? '' : ' wd-yn--bare'), hidden: true },
    ...(caption ? [h('span', { class: 'wd-yn-label' }, label)] : []), wrap);

  const render = () => {
    replaceChildren(wrap, [['No', false], ['Yes', true]].map(([text, v]) => h('button', {
      type: 'button',
      class: 'wd-unit-btn' + (v === val ? ' is-active' : ''),
      'aria-pressed': v === val ? 'true' : 'false',
      // ⚠ The BUSY guard is on the click, not on `disabled`. A disabled button drops out of the tab
      // order mid-interaction, which moves focus somewhere unrelated while a re-key runs; the
      // control simply declines instead, exactly as the checkbox did while `bgOfferBusy` was set.
      onClick: () => {
        if (busy || v === val) return;
        val = v;
        render();
        onChange?.(v);
      },
    }, text)));
  };
  render();

  return {
    el,
    get: () => val,
    /** Set the answer WITHOUT firing `onChange` — this is the state-restoring path (a resume, a
     *  re-ingest, or a refused flip putting the answer back), never a buyer's action. */
    set: (v) => { const n = !!v; if (n === val) return; val = n; render(); },
    setBusy: (b) => { busy = !!b; el.classList.toggle('is-busy', !!b); },
  };
}
